import React from 'react';
import type { BillingInterval, PlanDisplayEntry } from '../../types/billing';

/**
 * The current plan's price line — "$199/yr · Scaling orgs" plus the
 * Yearly/Monthly pill — shared by the Stripe page (`BillingPage.tsx`) and the
 * Shopify page (`ShopifyBillingView.tsx`).
 *
 * 🚨 It is shared because the copy of it is what broke. The two cards were
 * byte-identical markup maintained twice; when the Stripe one learned about
 * `billing_interval`, the Shopify one kept a HARDCODED '/mo' and showed every
 * yearly merchant the monthly price with the wrong suffix. One component means
 * the next interval change cannot land on one surface only.
 *
 * Two gates, deliberately different:
 *   - the PRICE SUFFIX stays gated on `priceNum != null`. Enterprise's price is
 *     the literal string "Custom", and "Custom/yr" is not a price.
 *   - the PILL is gated on the INTERVAL alone. The interval is a fact about the
 *     subscription, not about whether we can print a number beside it — an
 *     Enterprise workspace is on a yearly or a monthly contract just as
 *     definitely as a Starter one, and used to be shown neither.
 *
 * A missing `billingInterval` renders NO pill rather than guessing "Monthly".
 * The backend defaults the field to "month" server-side (`api/billing.py`), so
 * this only bites on a payload predating the field — where omitting is honest
 * and asserting is not.
 */
const PlanPriceLine: React.FC<{
  planDisplay: PlanDisplayEntry | null;
  billingInterval?: BillingInterval;
}> = ({ planDisplay, billingInterval }) => {
  const yearly = billingInterval === 'year';
  return (
    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
      {yearly ? planDisplay?.yearlyPrice : planDisplay?.price}
      <span className="text-xs">
        {planDisplay?.priceNum != null ? (yearly ? '/yr' : '/mo') : ''}
      </span>
      {billingInterval && (
        <span className="ml-2 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
          {yearly ? 'Yearly' : 'Monthly'}
        </span>
      )}
      {' · '}
      {planDisplay?.tagline}
    </p>
  );
};

export default PlanPriceLine;
